import { useState, useEffect } from 'react'
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { parseDate } from 'utils'
import { showAlert } from 'services/alerts.service'
import { makeRequest } from 'services/request.service'
import TableComponent from 'components/common/TableComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import ActionCardComponent from 'components/common/ActionCardComponent'
import ModalComponent from 'components/common/ModalComponent'

// Same format the backend enforces: gf-app's package.json version.
const SEMVER_REGEX = /^\d+\.\d+\.\d+$/

const EMPTY_FORM = { file: null, version: '', notes: '', mandatory: false }

const formatMegabytes = bytes => `${(Number(bytes || 0) / (1024 * 1024)).toFixed(1)} MB`

const AppReleases = () => {
  const { t } = useTranslation()
  const [state, setState] = useState({
    releases: [],
    isUploadModalOpen: false,
    form: EMPTY_FORM,
    uploading: false,
    progress: 0,
    updatingVersion: null
  })

  const getReleases = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: '/system/app-releases',
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      if (response.status === 200) {
        setState(s => ({ ...s, releases: response.data.releases || [] }))
      }
    } catch (error) {
      console.error('Error fetching app releases:', error)
      showAlert({ message: t('system.appReleases.fetchError'), icon: 'error' })
    }
  }

  useEffect(() => {
    getReleases()
  }, [])

  const openUploadModal = () => setState(s => ({ ...s, isUploadModalOpen: true, form: EMPTY_FORM, progress: 0 }))

  const closeUploadModal = () => {
    if (state.uploading) return
    setState(s => ({ ...s, isUploadModalOpen: false, form: EMPTY_FORM, progress: 0 }))
  }

  const handleFormChange = event => {
    const { name, type, checked, value, files } = event.target
    const parsedValue = type === 'checkbox' ? checked : type === 'file' ? (files?.[0] || null) : value
    setState(s => ({ ...s, form: { ...s.form, [name]: parsedValue } }))
  }

  const handleUpload = async () => {
    const { file, version, notes, mandatory } = state.form
    const trimmedVersion = version.trim()

    if (!file || !file.name.toLowerCase().endsWith('.apk')) {
      showAlert({ message: t('system.appReleases.fileRequired'), icon: 'warning' })
      return
    }
    if (!SEMVER_REGEX.test(trimmedVersion)) {
      showAlert({ message: t('system.appReleases.versionInvalid'), icon: 'warning' })
      return
    }

    // Text fields go first: the backend reads them before streaming the file.
    const data = new FormData()
    data.append('version', trimmedVersion)
    data.append('mandatory', mandatory ? 'true' : 'false')
    data.append('notes', notes.trim())
    data.append('apk', file)

    setState(s => ({ ...s, uploading: true, progress: 0 }))
    try {
      const response = await makeRequest({
        method: 'POST',
        url: '/system/app-releases',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`,
          'Content-Type': 'multipart/form-data'
        },
        data,
        onUploadProgress: event => {
          if (!event.total) return
          setState(s => ({ ...s, progress: Math.round((event.loaded / event.total) * 100) }))
        }
      })
      if (response.status === 200) {
        showAlert({ message: t('system.appReleases.uploadedOk', { version: trimmedVersion }), icon: 'success' })
        setState(s => ({ ...s, uploading: false, isUploadModalOpen: false, form: EMPTY_FORM, progress: 0 }))
        await getReleases()
      }
    } catch (error) {
      console.error('Error uploading app release:', error)
      setState(s => ({ ...s, uploading: false, progress: 0 }))
      showAlert({
        message: error?.response?.data?.message || t('system.appReleases.uploadError'),
        icon: 'error'
      })
    }
  }

  // Devices pick the change up on their next update check (app start/foreground).
  const handleToggleMandatory = async (release, mandatory) => {
    setState(s => ({ ...s, updatingVersion: release.version }))
    try {
      const response = await makeRequest({
        method: 'PATCH',
        url: `/system/app-releases/${release.version}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        data: { mandatory }
      })
      if (response.status === 200) {
        setState(s => ({
          ...s,
          releases: s.releases.map(r => r.version === release.version ? { ...r, mandatory } : r)
        }))
        showAlert({
          message: t(mandatory ? 'system.appReleases.mandatoryOn' : 'system.appReleases.mandatoryOff', { version: release.version }),
          icon: 'success'
        })
      }
    } catch (error) {
      console.error('Error updating app release:', error)
      showAlert({ message: error?.response?.data?.message || t('system.appReleases.updateError'), icon: 'error' })
    } finally {
      setState(s => ({ ...s, updatingVersion: null }))
    }
  }

  const latestVersion = state.releases[0]?.version

  const releasesTableData = {
    columns: [
      { key: 'version', labelKey: 'system.appReleases.cols.version' },
      {
        key: 'fileSize',
        labelKey: 'system.appReleases.cols.size',
        cell: value => formatMegabytes(value)
      },
      {
        key: 'mandatory',
        labelKey: 'system.appReleases.cols.mandatory',
        cell: (value, row) => (
          <label className='inline-flex items-center gap-2 cursor-pointer'>
            <input
              type='checkbox'
              checked={!!value}
              disabled={state.updatingVersion === row.version}
              onChange={e => handleToggleMandatory(row, e.target.checked)}
              title={t('system.appReleases.mandatoryHint')}
            />
            <span>{value ? t('system.appReleases.yes') : t('system.appReleases.no')}</span>
          </label>
        )
      },
      { key: 'notes', labelKey: 'system.appReleases.cols.notes', cell: value => value || '' },
      { key: 'createdBy', labelKey: 'system.appReleases.cols.createdBy', cell: value => value || '' },
      {
        key: 'createdAt',
        labelKey: 'system.appReleases.cols.createdAt',
        orderBy: 'desc',
        cell: value => parseDate(value)
      }
    ],
    rows: state.releases
  }

  return (
    <>
      <ActionCardComponent>
        <div data-align='left' className='flex flex-col'>
          <h3 className='text-lg font-semibold text-greenPrimary'>{t('system.appReleases.title')}</h3>
          <span className='text-sm text-gray-500'>
            {latestVersion
              ? t('system.appReleases.latest', { version: latestVersion })
              : t('system.appReleases.none')}
          </span>
        </div>
        <ButtonComponent onClick={openUploadModal}>
          <ArrowUpTrayIcon className='w-5 h-5' />
          <span className='font-medium'>{t('system.appReleases.upload')}</span>
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent tableData={releasesTableData} defaultSortColumn='createdAt' defaultSortOrder='desc' />

      <ModalComponent
        isOpen={state.isUploadModalOpen}
        onClose={closeUploadModal}
        title={t('system.appReleases.uploadTitle')}
        maxWidth='lg'
        hideDefaultFooter
      >
        <div className='flex flex-col gap-4'>
          <label className='block text-sm font-medium'>
            {t('system.appReleases.file')} *
            <input
              type='file'
              name='file'
              accept='.apk,application/vnd.android.package-archive'
              onChange={handleFormChange}
              disabled={state.uploading}
              className='w-full mt-1 p-2 border rounded'
            />
          </label>
          <label className='block text-sm font-medium'>
            {t('system.appReleases.version')} *
            <input
              type='text'
              name='version'
              placeholder={latestVersion || '1.0.0'}
              value={state.form.version}
              onChange={handleFormChange}
              disabled={state.uploading}
              className='w-full mt-1 p-2 border rounded'
            />
            <span className='text-xs text-gray-500'>{t('system.appReleases.versionHint')}</span>
          </label>
          <label className='block text-sm font-medium'>
            {t('system.appReleases.notes')}
            <textarea
              name='notes'
              rows={3}
              value={state.form.notes}
              onChange={handleFormChange}
              disabled={state.uploading}
              className='w-full mt-1 p-2 border rounded'
            />
          </label>
          <label className='flex items-start gap-2 text-sm'>
            <input
              type='checkbox'
              name='mandatory'
              checked={state.form.mandatory}
              onChange={handleFormChange}
              disabled={state.uploading}
              className='mt-1'
            />
            <span>
              <span className='font-medium'>{t('system.appReleases.mandatory')}</span>
              <br />
              <span className='text-gray-500'>{t('system.appReleases.mandatoryHint')}</span>
            </span>
          </label>

          {state.uploading && (
            <div>
              <div className='w-full bg-gray-200 rounded h-2'>
                <div className='bg-greenPrimary h-2 rounded transition-all' style={{ width: `${state.progress}%` }} />
              </div>
              <span className='text-xs text-gray-500'>{t('system.appReleases.uploading', { progress: state.progress })}</span>
            </div>
          )}

          <div className='flex justify-end gap-3 mt-2'>
            <button
              type='button'
              onClick={closeUploadModal}
              disabled={state.uploading}
              className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 disabled:opacity-50'
            >
              {t('mgmt.common.cancel')}
            </button>
            <button
              type='button'
              onClick={handleUpload}
              disabled={state.uploading}
              className='px-4 py-2 rounded bg-greenPrimary text-white hover:opacity-90 disabled:opacity-50'
            >
              {t('system.appReleases.upload')}
            </button>
          </div>
        </div>
      </ModalComponent>
    </>
  )
}

export default AppReleases
